// ABOUTME: API client for the team members the owner chats with beside Marketing — the
// roster of the business's team roles that are on (Image and Video Producer, Outreach), each
// role's live status, each one's direct chat, and the Outreach on/off switch. Backed by
// app/api/v1/agent_roles.py.

import { api, inBusiness } from './client'

export interface AgentRole {
  id: string
  business_id: string
  slug: string
  name: string
  status: 'active' | 'paused' | 'removed'
  created_at: string
  updated_at: string
}

export interface AgentRoleMessage {
  id: string
  direction: 'inbound' | 'outbound'
  sender_label: string | null
  content: string
  created_at: string
  /** The role's brief a post (or the owner's answer to it) belongs to; null in the direct chat. */
  production_brief_id?: string | null
}

/** A team role with where it stands now (app/services/agent_fleet/roster_status.py). */
export interface RoleRosterStatus {
  slug: string
  name: string
  role_id: string | null
  state: 'working' | 'idle' | 'off'
  working_on: string | null
  queued: number
  /** Used at least once (briefed, or its chat opened): the left panel lists it. */
  listed: boolean
}

const BASE_URL = '/agent-roles'

/** Prefix of every business's roster-status query; brief and run events invalidate it. */
export const ROSTER_STATUS_KEY = ['agent-roles', 'status'] as const

/** Every team role of `businessId`, on or off, with what it is doing now. */
export async function fetchRosterStatus(businessId: string): Promise<RoleRosterStatus[]> {
  const res = await api.get<RoleRosterStatus[]>(`${BASE_URL}/status`, inBusiness(businessId))
  return Array.isArray(res) ? res : []
}

export async function fetchAgentRoles(): Promise<AgentRole[]> {
  const res = await api.get<AgentRole[]>(BASE_URL)
  return Array.isArray(res) ? res : []
}

export async function fetchAgentRoleMessages(roleId: string): Promise<AgentRoleMessage[]> {
  const res = await api.get<{ messages: AgentRoleMessage[] }>(`${BASE_URL}/${roleId}/messages`)
  return res.messages ?? []
}

/** Prefix of every role's chat-thread query. */
export const AGENT_ROLE_MESSAGES_KEY = 'agent-role-messages'

/** Query key of one role's chat thread. */
export const agentRoleMessagesKey = (roleId: string) => [AGENT_ROLE_MESSAGES_KEY, roleId] as const

/** Query key holding the role's latest failed reply (set from the event stream). */
export const agentRoleReplyFailureKey = (roleId: string) =>
  ['agent-role-reply-failure', roleId] as const

export interface AgentRoleReplyFailure {
  error: string | null
  at: string
}

/**
 * Store the owner's message on the role's thread. Returns the stored message
 * at once; the role's reply arrives later on the same thread (event stream).
 * `clientMessageId` makes a retried send store nothing new.
 */
export async function sendAgentRoleMessage(
  roleId: string,
  message: string,
  clientMessageId: string
): Promise<AgentRoleMessage> {
  return api.post<AgentRoleMessage>(`${BASE_URL}/${roleId}/messages`, {
    message,
    client_message_id: clientMessageId,
  })
}

/** Clear the owner's chat with this team member: the thread then shows only what comes after. */
export async function clearAgentRoleMessages(roleId: string): Promise<{ cleared_at: string }> {
  return api.post<{ cleared_at: string }>(`${BASE_URL}/${roleId}/messages/clear`, {})
}

export interface OutreachRoleStatus {
  enabled: boolean
  /** The plan allows Outreach (Pro and above). */
  available: boolean
  role_id: string | null
}

export async function fetchOutreachRole(): Promise<OutreachRoleStatus> {
  return api.get<OutreachRoleStatus>(`${BASE_URL}/outreach`)
}

export async function setOutreachRole(enabled: boolean): Promise<OutreachRoleStatus> {
  return api.put<OutreachRoleStatus>(`${BASE_URL}/outreach`, { enabled })
}
